import { useState, useMemo, useCallback } from 'react';
import { fmoGeneratorService } from '../services/fmoGeneratorService';

/**
 * Custom hook for managing FMO generation state and operations
 * Handles input file generation, processing status, and ZIP file creation
 */
export const useFMOGeneration = (
  selectedRows,
  fileAnalysis,
  configurationQueue,
  getUniqueConfigurations,
  clusterspecs,
  refreshClusters,
  onFragmentationStart
) => {
  // Processing states
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [generatedEntryIds, setGeneratedEntryIds] = useState([]);
  const [showDownload, setShowDownload] = useState(false);
  const [zipfileId, setZipfileId] = useState(null);
  
  // Handle generating FMO input files
  const handleGenerateInputFiles = useCallback(async (zipfileName, setSnackbarMessage, setSnackbarOpen) => {
    // Check if all selected files have target information
    const filesWithoutTarget = selectedRows.filter(row => !row.target || row.target === '<none>' || row.target === '');
    if (filesWithoutTarget.length > 0) {
      setProcessingStatus(`Error: ${filesWithoutTarget.length} selected file${filesWithoutTarget.length !== 1 ? 's' : ''} missing target information. Please specify target in Pattern Extraction or Metadata Editing panels.`);
      return;
    }
    
    // Get unique configurations to process
    const configurationsToProcess = getUniqueConfigurations(configurationQueue);
    
    if (configurationsToProcess.length === 0) {
      if (setSnackbarMessage && setSnackbarOpen) {
        setSnackbarMessage('Please complete at least one configuration before generating');
        setSnackbarOpen(true);
      }
      return;
    }
    
    // Clear previous generation results
    setShowDownload(false);
    setGeneratedEntryIds([]);
    setProcessingStatus('');
    
    setIsProcessing(true);
    setProcessingStatus(`Starting FMO input generation for ${configurationsToProcess.length} configuration(s)...`);
    
    try {
      // Store all generated entries across all configurations
      const allGeneratedEntries = [];
      
      // Store groups and clusters that can be reused
      const targetGroups = {}; // Map of target -> group info
      const targetClusters = {}; // Map of target+clusterspec -> cluster ID
      
      // Process each configuration
      for (let configIndex = 0; configIndex < configurationsToProcess.length; configIndex++) {
        const config = configurationsToProcess[configIndex];
        setProcessingStatus(`Processing configuration ${configIndex + 1}/${configurationsToProcess.length}...`);
        
        // Process each target for this configuration
        for (const [target, files] of Object.entries(fileAnalysis.filesByTarget)) {
          
          // Get or create group (reuse if already created for this target)
          let groupInfo = targetGroups[target];
          if (!groupInfo) {
            setProcessingStatus(`Config ${configIndex + 1}: Creating/retrieving group for ${target}...`);
            
            const prepIds = files.map(f => f.id_ || f.id);
            groupInfo = await fmoGeneratorService.createGroup(prepIds, target);
            targetGroups[target] = groupInfo;
          }
          
          const groupId = groupInfo.id_;
          const groupMembers = groupInfo.members || [];
          
          // Handle cluster creation or selection
          let clusterId;
          
          if (config.clusterSelectionMode === 'existing') {
            // Use existing cluster
            clusterId = config.selectedCluster;
          } else {
            // Check if we already created a cluster for this target+clusterspec combination
            const clusterKey = `${target}_${config.selectedClusterspec}`;
            
            if (targetClusters[clusterKey]) {
              // Reuse existing cluster
              clusterId = targetClusters[clusterKey];
            } else {
              // Create new cluster
              setProcessingStatus(`Config ${configIndex + 1}: Creating cluster for ${target}...`);
              
              // Use the cluster name from config, which already contains the user's input or the default
              let clusterName = config.clusterName;
              if (!clusterName) {
                // Generate default name if somehow still null
                const clusterspec = clusterspecs.find(spec => spec.id_ === config.selectedClusterspec);
                const clusterspecName = clusterspec ? clusterspec.name : '';
                clusterName = fmoGeneratorService.generateDefaultClusterName({
                  target,
                  files,
                  clusterspecName
                });
              }
              
              clusterId = await fmoGeneratorService.createCluster({
                name: clusterName,
                clusterspecId: config.selectedClusterspec,
                groupId: groupId,
                target: target
              });
              
              // Store for reuse
              targetClusters[clusterKey] = clusterId;
            }
          }
          
          setProcessingStatus(`Config ${configIndex + 1}: Creating entries for ${target}...`);
          
          // Create entries using the service
          const newEntries = await fmoGeneratorService.createEntries({
            groupMembers,
            clusterId,
            pocketspecId: config.selectedPocketspec,
            inputspecId: config.selectedInputspec,
            target,
            configIndex
          });
          
          allGeneratedEntries.push(...newEntries);
        }
      }
      
      // Extract all entry IDs for download
      const entryIds = allGeneratedEntries.map(entry => entry.id_);
      setGeneratedEntryIds(entryIds);
      
      // Keep configuration queue after generation for convenience
      // Users can continue to modify or regenerate with different settings
      
      // Create zipfile with the generated entries
      try {
        const zipfile = await fmoGeneratorService.createZipfile(entryIds, zipfileName);
        setZipfileId(zipfile.id_);
        
        setProcessingStatus(`Successfully generated ${allGeneratedEntries.length} FMO input set(s) across ${configurationsToProcess.length} configuration(s) and created ZIP: ${zipfileName}`);
        setShowDownload(true);
      } catch (zipError) {
        console.error('Failed to create zipfile:', zipError);
        setProcessingStatus(`Warning: Files generated but ZIP creation failed: ${zipError.message}`);
        setShowDownload(false);
      }
      
      setIsProcessing(false);
      
      // Refresh clusters to include newly created ones in the "Use Previous Selection" list
      await refreshClusters();
      
      // Notify parent if callback provided
      if (onFragmentationStart) {
        onFragmentationStart(allGeneratedEntries);
      }
      
    } catch (error) {
      console.error('Failed to generate FMO inputs:', error);
      setProcessingStatus(`Error: ${error.response?.data?.detail || error.message}`);
      setIsProcessing(false);
    }
  }, [selectedRows, fileAnalysis, configurationQueue, getUniqueConfigurations, 
      clusterspecs, refreshClusters, onFragmentationStart]);
  
  // Handle downloading the created zipfile
  const handleDownloadZip = useCallback(async (zipfileId, zipfileName, setSnackbarMessage, setSnackbarOpen) => {
    if (!zipfileId) return;
    
    try {
      await fmoGeneratorService.downloadZipfile(zipfileId, zipfileName);
      if (setSnackbarMessage && setSnackbarOpen) {
        setSnackbarMessage(`Downloaded ${zipfileName} successfully`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to download ZIP:', error);
      setProcessingStatus(`Download failed: ${error.message}`);
    }
  }, []);
  
  // Generate default zipfile name
  const defaultZipfileName = useMemo(() => {
    return fmoGeneratorService.generateZipfileName(selectedRows);
  }, [selectedRows]);
  
  // Reset generation state
  const resetGenerationState = useCallback(() => {
    setIsProcessing(false);
    setProcessingStatus('');
    setGeneratedEntryIds([]);
    setShowDownload(false);
    setZipfileId(null);
  }, []);
  
  return {
    // Processing states
    isProcessing,
    processingStatus,
    setProcessingStatus,
    generatedEntryIds,
    setGeneratedEntryIds,
    showDownload,
    setShowDownload,
    zipfileId,
    setZipfileId,
    
    // Actions
    handleGenerateInputFiles,
    handleDownloadZip,
    defaultZipfileName,
    resetGenerationState
  };
};